/** SYSTEM HEALTH — shared presentational parts. */
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, Database, Server, Timer, Plug, ScrollText } from "lucide-react";

export const STATUS = {
  not_configured: { label: "Not configured (optional)", dot: "bg-slate-300", text: "text-slate-500", soft: "bg-slate-50", border: "border-slate-300" },
  operational: { label: "Operational", dot: "bg-[color:var(--st-success)]", text: "text-[color:var(--st-success)]", soft: "bg-[color:var(--st-success-soft)]", border: "border-[color:var(--st-success)]" },
  warning: { label: "Warning", dot: "bg-[color:var(--st-warn)]", text: "text-[color:var(--st-warn)]", soft: "bg-[color:var(--st-warn-soft)]", border: "border-[color:var(--st-warn)]" },
  degraded: { label: "Degraded", dot: "bg-orange-500", text: "text-orange-600", soft: "bg-orange-50", border: "border-orange-400" },
  failed: { label: "Failed", dot: "bg-[color:var(--st-critical)]", text: "text-[color:var(--st-critical)]", soft: "bg-[color:var(--st-critical-soft)]", border: "border-[color:var(--st-critical)]" },
};
export const ICON = { application: Server, database: Database, api: Activity, jobs: Timer, integrations: Plug, logs: ScrollText };

export const fmtSince = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
export const fmtUptime = (sec) => {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};

export function StatusDot({ status, ping = false, size = "h-2.5 w-2.5" }) {
  const t = STATUS[status] || STATUS.warning;
  return (
    <span className={`relative inline-flex ${size} rounded-full ${t.dot}`}>
      {ping && status === "operational" && <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${t.dot}`} />}
    </span>
  );
}

export function StatusPill({ status, testId }) {
  const t = STATUS[status] || STATUS.warning;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full border text-[10px] font-semibold uppercase tracking-[0.1em] ${t.soft} ${t.text} border-current/20`}>
      <StatusDot status={status} size="h-1.5 w-1.5" />{t.label}
    </span>
  );
}

export function ComponentCard({ c, active, onClick, index = 0 }) {
  const I = ICON[c.key] || Activity;
  const t = STATUS[c.status] || STATUS.warning;
  return (
    <button onClick={onClick} data-testid={`sh-card-${c.key}`} style={{ animationDelay: `${index * 60}ms` }}
      className={`os-card os-rise text-left p-4 flex flex-col gap-3 border-l-4 ${t.border} ${active ? "ring-2 ring-[color:var(--st-info)]" : ""}`}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg grid place-items-center bg-[color:var(--st-surface-alt)]"><I className="h-4 w-4" /></span>
        <span className="text-[13px] font-semibold flex-1">{c.label}</span>
        <StatusPill status={c.status} testId={`sh-card-${c.key}-status`} />
      </div>
      <div className="text-[12px] text-[color:var(--st-text-muted)] leading-snug" data-testid={`sh-card-${c.key}-detail`}>{c.detail}</div>
    </button>
  );
}

export function KV({ k, v, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-dashed border-[color:var(--st-border)] last:border-0 text-[12px]">
      <span className="text-[color:var(--st-text-muted)]">{k}</span>
      <span className="font-mono font-medium text-right" data-testid={testId}>{v ?? "—"}</span>
    </div>
  );
}

export function TrafficChart({ data }) {
  return (
    <div className="h-[180px]" data-testid="sh-traffic-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="shReq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#246BFD" stopOpacity={0.35} /><stop offset="100%" stopColor="#246BFD" stopOpacity={0} /></linearGradient>
            <linearGradient id="shErr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C2333F" stopOpacity={0.5} /><stop offset="100%" stopColor="#C2333F" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
          <XAxis dataKey="t" tick={{ fontSize: 10 }} interval={9} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Area type="monotone" dataKey="requests" name="Requests" stroke="#246BFD" fill="url(#shReq)" strokeWidth={1.5} />
          <Area type="monotone" dataKey="errors" name="5xx errors" stroke="#C2333F" fill="url(#shErr)" strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Table({ cols, rows, empty, testId, rowKey }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-[12px]">
        <thead><tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[color:var(--st-text-subtle)]">{cols.map((c) => <th key={c.k} className="py-2 pr-3 font-semibold">{c.h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="py-6 text-center text-[color:var(--st-text-muted)]">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={rowKey ? rowKey(r, i) : i} className="border-t border-[color:var(--st-border)] align-top hover:bg-[color:var(--st-surface-alt)]/60">
              {cols.map((c) => <td key={c.k} className="py-2 pr-3">{c.r ? c.r(r) : r[c.k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
